<!--
  ~ Copyright (c) 2023 D2ArmorPicker by Mijago.
  ~
  ~ This program is free software: you can redistribute it and/or modify
  ~ it under the terms of the GNU Affero General Public License as published
  ~ by the Free Software Foundation, either version 3 of the License, or
  ~ (at your option) any later version.
  ~
  ~ This program is distributed in the hope that it will be useful,
  ~ but WITHOUT ANY WARRANTY; without even the implied warranty of
  ~ MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
  ~ GNU Affero General Public License for more details.
  ~
  ~ You should have received a copy of the GNU Affero General Public License
  ~ along with this program.  If not, see <http://www.gnu.org/licenses/>.
  -->
<div *ngVar="status.status | async as stat">
  <mat-toolbar *ngIf="stat.apiError" color="warn" class="bar-api-warn">
    <span class="header-spacer"></span>
    The API is unavailable. The app will work with the data it has available right now.
    <span class="header-spacer"></span>
  </mat-toolbar>
  <div *ngVar="status.status | async as stat">
    <mat-toolbar *ngIf="stat.authError" color="warn" class="bar-api-warn">
      <span class="header-spacer"></span>
      There was an error with the authentication. The API may be down, or your session timed out.
      <span class="header-spacer"></span>
      <!--You can either wait and try again later - the app will use the data it has available right now - or you can logout.-->
    </mat-toolbar>
  </div>

  <mat-toolbar id="header" role="heading">
    <!-- Mobile Menu Toggle -->
    <button
      *ngIf="isHandset$ | async"
      (click)="drawer.toggle()"
      aria-label="Toggle sidenav"
      mat-icon-button
      type="button">
      <mat-icon>menu</mat-icon>
    </button>

    <!-- App Title and Social Links -->
    <span>
      D2ArmorPicker <small>by Mijago</small>
      <a
        href="https://twitter.com/MijagoCoding/"
        matTooltip="Follow me on Twitter"
        target="_blank"
        rel="noopener noreferrer">
        <img alt="Twitter logo" class="logo-twitter" src="assets/logo-twitter.png" />
      </a>
      <a
        href="https://ko-fi.com/mijago"
        matTooltip="Buy me a coffee on Ko-Fi!"
        target="_blank"
        rel="noopener noreferrer">
        <img alt="Ko-Fi logo" class="logo-kofi" src="assets/logo-kofi.png" />
      </a>
      <a
        href="http://discord.d2armorpicker.com/"
        matTooltip="Join the Discord for support and development!"
        target="_blank"
        rel="noopener noreferrer">
        <img alt="Discord logo" class="logo-discord" src="assets/logo-discord.png" />
      </a>
    </span>

    <span class="header-spacer"></span>

    <!-- Desktop Navigation -->
    <div class="hide-sm">
      <nav id="navbar" mat-tab-nav-bar [tabPanel]="tabPanel">
        <a
          *ngFor="let link of navLinks; let idx = index"
          [active]="activeLinkIndex === idx"
          [routerLink]="link.link"
          mat-tab-link>
          {{ link.name }}
        </a>
      </nav>
      <mat-tab-nav-panel #tabPanel></mat-tab-nav-panel>
    </div>

    <span class="header-spacer"></span>

    <!-- Header Action Buttons -->
    <div id="buttonContainer">
      <a
        (click)="changelog.openChangelogDialog()"
        style="color: white"
        class="changelogText hide-sm"
        matTooltip="View the changelog dialog.">
        v{{ version }}
      </a>

      <a
        color="accent"
        class="hide-sm"
        href="https://ko-fi.com/mijago"
        mat-stroked-button
        matTooltip="Support me on Ko-Fi!"
        target="_blank"
        rel="noopener noreferrer">
        <mat-icon inline>attach_money</mat-icon>
        Buy me a coffee!
      </a>

      <button
        (click)="refreshInventory()"
        aria-label="Refresh inventory"
        class="example-icon"
        mat-icon-button
        matTooltip="Reload inventory from the API.">
        <mat-icon>inventory_2</mat-icon>
      </button>

      <button
        (click)="refreshAll(true)"
        aria-label="Refresh"
        class="example-icon"
        mat-icon-button
        matTooltip="Reload all items from the API.">
        <mat-icon>refresh</mat-icon>
      </button>

      <button
        (click)="logout()"
        aria-label="Logout"
        class="example-icon"
        color="warn"
        mat-icon-button
        matTooltip="Logout">
        <mat-icon>logout</mat-icon>
      </button>
    </div>
  </mat-toolbar>

  <span *ngVar="status.status | async as stat">
    <mat-progress-bar
      *ngIf="
        stat.updatingResultsTable ||
        stat.calculatingResults ||
        stat.calculatingPermutations ||
        stat.updatingInventory ||
        stat.updatingVendors ||
        stat.updatingManifest
      "
      id="loading"
      mode="indeterminate"></mat-progress-bar>
  </span>

  <router-outlet></router-outlet>

  <div *ngVar="status.status | async as stat" class="info-container">
    <mat-card appearance="outlined" *ngIf="stat.updatingVendors" class="info info-updating-vendor">
      <mat-card-content>Updating vendor information inventory in the background.</mat-card-content>
    </mat-card>
    <mat-card
      appearance="outlined"
      *ngIf="stat.updatingManifest"
      class="info info-updating-manifest">
      <mat-card-content
        >Please wait:<br />
        Updating the cached manifest.
      </mat-card-content></mat-card
    >
    <mat-card
      appearance="outlined"
      *ngIf="stat.updatingInventory"
      class="info info-updating-inventory">
      <mat-card-content>
        Please wait:<br />
        Updating the cached inventory.
      </mat-card-content></mat-card
    >
    <mat-card
      appearance="outlined"
      *ngIf="stat.calculatingResults"
      class="info info-calculating-results">
      <mat-card-content
        >Please wait:<br />
        Calculating possible results.
        <mat-progress-bar [value]="computationProgress"></mat-progress-bar>
      </mat-card-content>
    </mat-card>
    <mat-card
      appearance="outlined"
      *ngIf="stat.updatingResultsTable"
      class="info info-updating-results">
      <mat-card-content>Please wait:<br />Updating the result display table. </mat-card-content>
    </mat-card>
  </div>

  <!-- Mobile Navigation Drawer -->
  <mat-sidenav-container>
    <mat-sidenav
      #drawer
      [attr.role]="(isHandset$ | async) ? 'dialog' : 'navigation'"
      [opened]="false"
      class="sidenav"
      fixedInViewport
      mode="over">
      <mat-sidenav-content>
        <mat-toolbar>
          <button
            (click)="drawer.toggle()"
            aria-label="Toggle sidenav"
            mat-stroked-button
            type="button">
            <mat-icon aria-label="Side nav toggle icon">menu</mat-icon>
            Close Menu
          </button>
        </mat-toolbar>

        <hr />

        <mat-nav-list>
          <a
            *ngFor="let link of navLinks; let idx = index"
            (click)="drawer.toggle()"
            [class.active]="activeLinkIndex === idx"
            [routerLink]="link.link"
            mat-list-item>
            {{ link.name }}
          </a>

          <hr />

          <a href="https://ko-fi.com/mijago" mat-list-item>
            <a
              color="primary"
              mat-raised-button
              matTooltip="You like my work? This leads you to ko-fi.com, where you can easily support me!"
              target="_blank">
              <mat-icon inline>attach_money</mat-icon>
              Buy me a coffee!
            </a>
          </a>

          <a (click)="changelog.openChangelogDialog()" mat-list-item>
            <a mat-raised-button>Open changelog</a>
          </a>

          <hr />
        </mat-nav-list>
      </mat-sidenav-content>
    </mat-sidenav>
  </mat-sidenav-container>
</div>
